import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";

function dateLabel(value) {
  if (!value) return "la fecha indicada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "la fecha indicada";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "long" }).format(date);
}

export default function BillingAccessBanner({ project }) {
  const navigate = useNavigate();
  const location = useLocation();
  const access = getProjectBillingAccess(project);
  const projectId = project?.slug || project?.id || project?._id;

  if (!projectId || (!access.isGrace && !access.isLandingOnly)) return null;

  const billingPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;
  const alreadyInBilling = location.pathname.includes("/configuracion/suscripcion");
  if (alreadyInBilling) return null;

  if (access.isGrace) {
    const daysText = access.daysRemaining === 1
      ? "Queda 1 día"
      : `Quedan ${access.daysRemaining ?? "pocos"} días`;

    return (
      <Alert
        severity="warning"
        icon={<WarningAmberRoundedIcon />}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid rgba(237,108,2,.25)",
          alignItems: "center",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "center" }}
          spacing={2}
        >
          <Box>
            <AlertTitle sx={{ fontWeight: 950, mb: 0.25 }}>
              No pudimos cobrar la última mensualidad
            </AlertTitle>
            <Typography variant="body2">
              Tu sistema sigue funcionando normalmente hasta el {dateLabel(access.graceUntil)}. {daysText} para actualizar la tarjeta sin interrupciones.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="warning"
            startIcon={<CreditCardRoundedIcon />}
            onClick={() => navigate(billingPath)}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950, flexShrink: 0 }}
          >
            Resolver pago
          </Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      severity="error"
      icon={<LockClockRoundedIcon />}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: "1px solid rgba(211,47,47,.22)",
        alignItems: "center",
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        spacing={2}
      >
        <Box>
          <AlertTitle sx={{ fontWeight: 950, mb: 0.25 }}>
            Las funciones operativas están temporalmente pausadas
          </AlertTitle>
          <Typography variant="body2">
            La landing continúa visible y tus datos están seguros. Regularizá el pago para reactivar automáticamente productos, pedidos, cobros, estadísticas y sistema.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<CreditCardRoundedIcon />}
          onClick={() => navigate(billingPath)}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950, flexShrink: 0 }}
        >
          Reactivar ahora
        </Button>
      </Stack>
    </Alert>
  );
}
